import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Play, Calendar, ArrowRight, X, Clock, Tag, Loader2, RefreshCw } from 'lucide-react';

const Palavras = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // URL do canal MEVAM Brasília (apenas o @ do canal)
  const CHANNEL_HANDLE = '@mevam.brasilia';
  
  // Função para categorizar vídeos baseado no título
  const categorizeVideo = (title) => {
    const titleLower = title.toLowerCase();
    if (titleLower.includes('jovem') || titleLower.includes('rede de jovens')) {
      return 'Jovens';
    } else if (titleLower.includes('imersão') || titleLower.includes('evento') || titleLower.includes('conferência')) {
      return 'Eventos';
    } else if (titleLower.includes('mensagem') || titleLower.includes('pregação')) {
      return 'Mensagens';
    } else {
      return 'Cultos';
    }
  };

  // Função para formatar data
  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
    return `${date.getDate()} de ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Função para extrair ID do vídeo do link do YouTube
  const extractVideoId = (link) => {
    const match = link.match(/watch\?v=([^&]+)/);
    return match ? match[1] : null;
  };

  // Buscar vídeos do YouTube via RSS Feed (SEM API!)
  const fetchYouTubeVideos = async () => {
    try {
      setLoading(true);
      setError(null);

      // Primeiro, tentar buscar o ID do canal a partir do handle
      // Usamos um proxy CORS para evitar problemas de CORS
      const corsProxy = 'https://api.allorigins.win/raw?url=';
      const channelPageUrl = `https://www.youtube.com/${CHANNEL_HANDLE}`;
      
      let channelId = null;
      
      try {
        const pageResponse = await fetch(corsProxy + encodeURIComponent(channelPageUrl));
        const pageText = await pageResponse.text();
        
        // Procurar pelo channelId no HTML da página
        const channelIdMatch = pageText.match(/"channelId":"([^"]+)"/);
        if (channelIdMatch) {
          channelId = channelIdMatch[1];
        }
      } catch (err) {
        console.warn('Não foi possível obter ID do canal automaticamente');
      }

      // Se não encontrou o ID, usar ID padrão (você pode atualizar isso manualmente)
      if (!channelId) {
        // Para encontrar o ID do canal:
        // 1. Acesse: https://www.youtube.com/@mevam.brasilia
        // 2. Veja o código fonte (Ctrl+U)
        // 3. Procure por "channelId"
        channelId = 'UC2ILVabVRlfIsD8GYd4Rflw';
      }

      // Buscar RSS Feed do canal (não precisa de API Key!)
      const rssFeedUrl = `https://www.youtube.com/feeds/videos.xml?channel_id=${channelId}`;
      const rssResponse = await fetch(corsProxy + encodeURIComponent(rssFeedUrl));
      const rssText = await rssResponse.text();

      // Parse do XML
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(rssText, 'text/xml');
      
      // Verificar se há erro no parsing
      if (xmlDoc.querySelector('parsererror')) {
        throw new Error('Erro ao processar feed do YouTube');
      }

      // Extrair vídeos do feed
      const entries = xmlDoc.querySelectorAll('entry');
      
      if (entries.length === 0) {
        console.warn('Nenhum vídeo encontrado no feed. Usando vídeos de exemplo.');
        loadExampleVideos();
        return;
      }

      const formattedVideos = Array.from(entries).map((entry, index) => {
        const title = entry.querySelector('title')?.textContent || 'Sem título';
        const link = entry.querySelector('link')?.getAttribute('href') || '';
        const published = entry.querySelector('published')?.textContent || '';
        const videoId = extractVideoId(link) || entry.querySelector('videoId')?.textContent || '';
        
        // Media group para thumbnail e descrição
        const mediaGroup = entry.querySelector('group');
        const description = mediaGroup?.querySelector('description')?.textContent || 
                          'Assista esta palavra transformadora';
        const thumbnail = mediaGroup?.querySelector('thumbnail')?.getAttribute('url') || 
                        `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

        return {
          id: index + 1,
          videoId: videoId,
          titulo: title,
          data: formatDate(published),
          duracao: '—', // RSS não fornece duração, mas não é problema
          thumbnail: thumbnail,
          categoria: categorizeVideo(title),
          descricao: description.split('\n')[0] || 'Assista esta palavra transformadora',
          pregador: 'MEVAM Ceilândia'
        };
      });

      setVideos(formattedVideos);
      setLoading(false);

      // Salvar no localStorage para cache
      localStorage.setItem('mevam_videos', JSON.stringify(formattedVideos));
      localStorage.setItem('mevam_videos_timestamp', Date.now().toString());

    } catch (err) {
      console.error('Erro ao buscar vídeos:', err);
      setError(err.message);
      
      // Tentar carregar do cache
      const cachedVideos = localStorage.getItem('mevam_videos');
      if (cachedVideos) {
        console.log('Usando vídeos do cache...');
        setVideos(JSON.parse(cachedVideos));
      } else {
        console.log('Usando vídeos de exemplo...');
        loadExampleVideos();
      }
      setLoading(false);
    }
  };

  // Carregar vídeos de exemplo (fallback)
  const loadExampleVideos = () => {
    const palavrasExemplo = [
      {
        id: 1,
        titulo: 'Celebração da Família 05 de Outubro 2025',
        data: '05 de out 2025',
        duracao: '2:22:00',
        thumbnail: 'https://img.youtube.com/vi/yhDWI6DjH10/maxresdefault.jpg',
        categoria: 'Cultos',
        videoId: 'yhDWI6DjH10',
        descricao: 'Culto de celebração da família com louvor, adoração e mensagem transformadora.',
        pregador: 'MEVAM Ceilândia'
      },
      {
        id: 2,
        titulo: 'Culto Profético 02 de outubro 2025',
        data: '02 de out 2025',
        duracao: '1:30:00',
        thumbnail: 'https://img.youtube.com/vi/_0I4Yi_7MZY/maxresdefault.jpg',
        categoria: 'Cultos',
        videoId: '_0I4Yi_7MZY',
        descricao: 'Momento especial de busca profética e palavra revelada.',
        pregador: 'MEVAM Ceilândia'
      },
      {
        id: 3,
        titulo: 'Celebração da Família 28 de setembro',
        data: '28 de set 2025',
        duracao: '2:24:00',
        thumbnail: 'https://img.youtube.com/vi/qa4hqhrbwdU/maxresdefault.jpg',
        categoria: 'Cultos',
        videoId: 'qa4hqhrbwdU',
        descricao: 'Culto especial de celebração com toda a família MEVAM.',
        pregador: 'MEVAM Ceilândia'
      },
      {
        id: 4,
        titulo: 'Culto Profético - 25 de Setembro 2025',
        data: '25 de set 2025',
        duracao: '1:27:00',
        thumbnail: 'https://img.youtube.com/vi/TbKkk6sI4jg/maxresdefault.jpg',
        categoria: 'Cultos',
        videoId: 'TbKkk6sI4jg',
        descricao: 'Palavra profética e momento de oração e intercessão.',
        pregador: 'MEVAM Ceilândia'
      },
      {
        id: 5,
        titulo: 'Culto da Família - 21 de Setembro 2025',
        data: '21 de set 2025',
        duracao: '2:42:00',
        thumbnail: 'https://img.youtube.com/vi/DiigpV3TMpQ/maxresdefault.jpg',
        categoria: 'Cultos',
        videoId: 'DiigpV3TMpQ',
        descricao: 'Celebração dominical com louvor, adoração e ministração da Palavra.',
        pregador: 'MEVAM Ceilândia'
      },
      {
        id: 6,
        titulo: 'Celebração da Família 14 de setembro (Santa Ceia)',
        data: '14 de set 2025',
        duracao: '2:33:00',
        thumbnail: 'https://img.youtube.com/vi/sFqRkhqfKVc/maxresdefault.jpg',
        categoria: 'Cultos',
        videoId: 'sFqRkhqfKVc',
        descricao: 'Culto especial de Santa Ceia com toda a família da fé.',
        pregador: 'MEVAM Ceilândia'
      },
      {
        id: 7,
        titulo: 'Celebração da Família 31 de Agosto - CNV Brasília',
        data: '31 de ago 2025',
        duracao: '2:44:00',
        thumbnail: 'https://img.youtube.com/vi/DGqegGik9ZU/maxresdefault.jpg',
        categoria: 'Cultos',
        videoId: 'DGqegGik9ZU',
        descricao: 'Culto de celebração com mensagem poderosa e momentos de adoração.',
        pregador: 'MEVAM Ceilândia'
      },
      {
        id: 8,
        titulo: 'Culto dos Jovens - 16/08',
        data: '16 de ago 2025',
        duracao: '1:35:00',
        thumbnail: 'https://img.youtube.com/vi/0tTAXiLJAqw/maxresdefault.jpg',
        categoria: 'Jovens',
        videoId: '0tTAXiLJAqw',
        descricao: 'Culto especial com a juventude MEVAM, louvor e palavra direcionada.',
        pregador: 'Rede de Jovens'
      },
      {
        id: 9,
        titulo: 'Culto de Santa Ceia - 10/08',
        data: '10 de ago 2025',
        duracao: '2:29:00',
        thumbnail: 'https://img.youtube.com/vi/4b2kH06fj0U/maxresdefault.jpg',
        categoria: 'Cultos',
        videoId: '4b2kH06fj0U',
        descricao: 'Momento solene de Santa Ceia e comunhão no corpo de Cristo.',
        pregador: 'MEVAM Ceilândia'
      },
      {
        id: 10,
        titulo: 'Rede de Jovens 19/07/2025 - CNV',
        data: '19 de jul 2025',
        duracao: '1:31:00',
        thumbnail: 'https://img.youtube.com/vi/qbxkkzXACPY/maxresdefault.jpg',
        categoria: 'Jovens',
        videoId: 'qbxkkzXACPY',
        descricao: 'Encontro da Rede de Jovens com louvor, palavra e comunhão.',
        pregador: 'Rede de Jovens'
      },
      {
        id: 11,
        titulo: 'Culto De Jovem - 19/04/2025 CNV',
        data: '19 de abr 2025',
        duracao: '1:59:00',
        thumbnail: 'https://img.youtube.com/vi/46ylcLG17VI/maxresdefault.jpg',
        categoria: 'Jovens',
        videoId: '46ylcLG17VI',
        descricao: 'Culto dinâmico e cheio de vida com a galera jovem.',
        pregador: 'Rede de Jovens'
      },
      {
        id: 12,
        titulo: 'Imersão em Deus - 06/04/25 - Último Período',
        data: '06 de abr 2025',
        duracao: '2:05:00',
        thumbnail: 'https://img.youtube.com/vi/qDMLUSqLtiI/maxresdefault.jpg',
        categoria: 'Eventos',
        videoId: 'qDMLUSqLtiI',
        descricao: 'Momento final da Imersão em Deus com adoração e palavra.',
        pregador: 'MEVAM Ceilândia'
      }
    ];
    
    setVideos(palavrasExemplo);
    setLoading(false);
  };

  // Buscar vídeos ao carregar a página
  useEffect(() => {
    // Verificar se há cache recente (menos de 1 hora)
    const cachedVideos = localStorage.getItem('mevam_videos');
    const cacheTimestamp = localStorage.getItem('mevam_videos_timestamp');
    const oneHour = 60 * 60 * 1000;

    if (cachedVideos && cacheTimestamp && (Date.now() - parseInt(cacheTimestamp)) < oneHour) {
      // Usar cache se for recente
      setVideos(JSON.parse(cachedVideos));
      setLoading(false);
    } else {
      // Buscar novos vídeos
      fetchYouTubeVideos();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categorias = ['Todas', 'Cultos', 'Jovens', 'Eventos', 'Mensagens'];

  const filteredVideos = videos.filter(video => {
    const matchesSearch = video.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         video.categoria.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         video.pregador.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'Todas' || video.categoria === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
  };

  return (
    <div className="min-h-screen bg-[#030B2E]">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-pattern"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Palavras
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Mensagens transformadoras que edificam, inspiram e renovam sua fé
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#B8C4E3]" size={20} />
                <input
                  type="text"
                  placeholder="Busque por título, categoria ou pregador..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-[#B8C4E3] focus:outline-none focus:ring-2 focus:ring-[#17E6B3] transition-all"
                />
              </div>
            </div>

            {/* Filtros de Categoria */}
            <div className="flex flex-wrap justify-center gap-3">
              {categorias.map((categoria) => (
                <button
                  key={categoria}
                  onClick={() => setSelectedCategory(categoria)}
                  className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                    selectedCategory === categoria
                      ? 'bg-[#17E6B3] text-[#030B2E] shadow-lg shadow-[#17E6B3]/30'
                      : 'bg-white/10 text-[#B8C4E3] hover:bg-white/20 border border-white/20'
                  }`}
                >
                  {categoria}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Videos Grid */}
      <section className="py-16">
        <div className="container-custom">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">
                {selectedCategory === 'Todas' ? 'Todas as Palavras' : selectedCategory}
              </h2>
              <p className="text-[#B8C4E3]">
                {filteredVideos.length} {filteredVideos.length === 1 ? 'palavra encontrada' : 'palavras encontradas'}
              </p>
            </div>
            <div className="flex gap-3">
              {error && (
                <button
                  onClick={fetchYouTubeVideos}
                  className="hidden md:flex items-center gap-2 px-6 py-3 bg-[#17E6B3]/20 hover:bg-[#17E6B3]/30 text-[#17E6B3] rounded-xl border border-[#17E6B3]/50 hover:border-[#17E6B3] transition-all"
                >
                  <RefreshCw size={20} />
                  Tentar Novamente
                </button>
              )}
              <a 
                href="https://www.youtube.com/@mevam.brasilia" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-[#17E6B3]/20 text-white hover:text-[#17E6B3] rounded-xl border border-white/20 hover:border-[#17E6B3]/50 transition-all"
              >
                Ver no YouTube
                <ArrowRight size={20} />
              </a>
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-20">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-white/5 flex items-center justify-center">
                <Loader2 className="text-[#17E6B3] animate-spin" size={40} />
              </div>
              <p className="text-white text-xl mb-2">
                Carregando palavras...
              </p>
              <p className="text-[#B8C4E3]">
                Buscando os vídeos mais recentes do canal
              </p>
            </div>
          ) : filteredVideos.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-white/5 flex items-center justify-center">
                <Search className="text-[#B8C4E3]" size={40} />
              </div>
              <p className="text-white text-xl mb-2">
                Nenhuma palavra encontrada
              </p>
              <p className="text-[#B8C4E3]">
                Tente buscar por outros termos ou categorias
              </p>
            </div>
          ) : (
            <>
            
              

              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVideos.map((video, index) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group cursor-pointer"
                  onClick={() => handleVideoClick(video)}
                >
                  <div className="relative overflow-hidden rounded-2xl bg-[#030B2E]/80 border border-white/10 hover:border-[#17E6B3]/50 transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-[#17E6B3]/20 group-hover:scale-[1.02]">
                    {/* Thumbnail */}
                    <div className="relative aspect-video bg-gradient-to-br from-[#030B2E] via-[#17E6B3]/10 to-[#030B2E] overflow-hidden">
                      <img 
                        src={video.thumbnail} 
                        alt={video.titulo}
                        className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#17E6B3]/90 group-hover:scale-110 transition-all duration-300">
                          <Play className="text-white ml-1" size={28} fill="white" />
                        </div>
                      </div>
                      
                      {/* Categoria Badge */}
                      <div className="absolute top-3 left-3">
                        <span className="flex items-center gap-1 px-3 py-1 bg-[#17E6B3]/90 backdrop-blur-sm text-[#030B2E] text-xs font-semibold rounded-full">
                          <Tag size={12} />
                          {video.categoria}
                        </span>
                      </div>

                      {/* Duração */}
                      <div className="absolute bottom-3 right-3">
                        <div className="flex items-center gap-1 px-2 py-1 bg-black/70 backdrop-blur-sm rounded-lg">
                          <Clock size={14} className="text-white" />
                          <span className="text-white text-xs font-medium">{video.duracao}</span>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar size={14} className="text-[#B8C4E3]" />
                        <span className="text-[#B8C4E3] text-sm">{video.data}</span>
                      </div>
                      <h3 className="text-white font-bold text-lg mb-2 line-clamp-2 group-hover:text-[#17E6B3] transition-colors">
                        {video.titulo}
                      </h3>
                      <p className="text-[#B8C4E3] text-sm line-clamp-2 mb-3">
                        {video.descricao}
                      </p>
                      <div className="flex items-center justify-between pt-3 border-t border-white/10">
                        <span className="text-white text-sm font-medium">{video.pregador}</span>
                        <ArrowRight size={16} className="text-[#17E6B3] group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
              </div>
            </>
          )}

          {/* CTA para Canal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-16"
          >
            <div className="bg-gradient-to-r from-[#17E6B3]/20 via-[#030B2E]/40 to-[#17E6B3]/20 border border-[#17E6B3]/30 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">
                Assista mais conteúdos no nosso canal
              </h3>
              <p className="text-[#B8C4E3] mb-6 max-w-2xl mx-auto">
                Inscreva-se no nosso canal do YouTube para receber notificações sobre novas mensagens e conteúdos exclusivos
              </p>
              <a
                href="https://www.youtube.com/@mevam.brasilia"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#17E6B3] hover:bg-[#17E6B3]/90 text-[#030B2E] font-semibold rounded-xl hover:shadow-xl hover:shadow-[#17E6B3]/30 transition-all duration-300"
              >
                <Play size={20} fill="#030B2E" />
                Visitar Canal do YouTube
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modal do Vídeo */}
      <AnimatePresence>
        {selectedVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            onClick={closeVideoModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 20 }}
              className="relative w-full max-w-6xl bg-[#030B2E] rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeVideoModal}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm flex items-center justify-center text-white transition-all group"
              >
                <X size={24} className="group-hover:rotate-90 transition-transform" />
              </button>

              {/* Video Player */}
              <div className="relative aspect-video bg-black">
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${selectedVideo.videoId}?autoplay=1`}
                  title={selectedVideo.titulo}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>

              {/* Video Info */}
              <div className="p-6 border-t border-white/10">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-3 py-1 bg-[#17E6B3]/20 text-[#17E6B3] text-sm font-semibold rounded-full border border-[#17E6B3]/30">
                        {selectedVideo.categoria}
                      </span>
                      <div className="flex items-center gap-2 text-[#B8C4E3] text-sm">
                        <Calendar size={14} />
                        <span>{selectedVideo.data}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#B8C4E3] text-sm">
                        <Clock size={14} />
                        <span>{selectedVideo.duracao}</span>
                      </div>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-3">
                      {selectedVideo.titulo}
                    </h2>
                    <p className="text-[#B8C4E3] mb-4">
                      {selectedVideo.descricao}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-[#B8C4E3]">Por:</span>
                      <span className="text-white font-semibold">{selectedVideo.pregador}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <a
                    href={`https://www.youtube.com/watch?v=${selectedVideo.videoId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 px-6 py-3 bg-[#17E6B3] hover:bg-[#17E6B3]/90 text-[#030B2E] font-semibold rounded-xl hover:shadow-lg hover:shadow-[#17E6B3]/30 transition-all text-center"
                  >
                    Assistir no YouTube
                  </a>
                  <button
                    onClick={closeVideoModal}
                    className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all border border-white/20"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Palavras;
